/**
 * ApiBase -- Authenticated fetch wrapper for NexoAPI.
 * Injects Authorization header, handles JSON, normalizes errors,
 * and retries on 429 (Too Many Requests) with exponential backoff.
 *
 * @module infrastructure/api/ApiBase
 */

import { AppConfig } from '../../config.js';
import { ok, fail } from '../../domain/value-objects/CrudResult.js';

const MAX_RETRIES = 3;
const BASE_DELAY_MS = 800;

async function request(method, path, body, attempt = 0, options = {}) {
  const url = `${AppConfig.apiBase}${path}`;
  const headers = { 'Content-Type': 'application/json' };

  if (AppConfig.token) {
    headers['Authorization'] = `Bearer ${AppConfig.token}`;
  }
  // MADERINCO-REDISENO-T1: cabeceras de un paso (If-Match con la revision vista). Nunca pisan la sesion.
  if (options.headers) {
    for (const [k, v] of Object.entries(options.headers)) {
      if (v !== undefined && v !== null && k.toLowerCase() !== 'authorization') headers[k] = String(v);
    }
  }

  const opts = { method, headers };
  if (body && method !== 'GET') {
    opts.body = JSON.stringify(body);
  }
  // Soporte opcional de AbortController/timeout (backwards-compatible)
  if (options.signal) {
    opts.signal = options.signal;
  }

  let res;
  try {
    res = await fetch(url, opts);
  } catch (err) {
    // Si fue abort, no reintentar
    if (err?.name === 'AbortError') {
      return fail('TIMEOUT', 'Solicitud cancelada (timeout)');
    }
    if (attempt < MAX_RETRIES) {
      await sleep(BASE_DELAY_MS * Math.pow(2, attempt));
      return request(method, path, body, attempt + 1, options);
    }
    return fail('NETWORK', err.message || 'Error de red');
  }

  // UN PIN FRENADO NO SE REINTENTA (PANEL-PIN-UNIFICADO-T1): el freno del PIN dura 60 s o mas y la cabecera
  // Retry-After no cruza al panel (la API no la expone), asi que reintentar solo deja al kiosko ~6 s en "..." para
  // terminar en el mismo 429. Quien pide { reintentar429: false } recibe el 429 de una y se lo dice a la persona.
  if (res.status === 429 && attempt < MAX_RETRIES && options.reintentar429 !== false) {
    const retryAfter = parseInt(res.headers.get('Retry-After') || '0', 10);
    const delay = retryAfter > 0 ? retryAfter * 1000 : BASE_DELAY_MS * Math.pow(2, attempt);
    await sleep(delay);
    return request(method, path, body, attempt + 1, options);
  }

  if (res.status === 401) {
    AppConfig.logout();
    window.location.reload();
    return fail('UNAUTHORIZED', 'Sesion expirada');
  }

  const data = await res.json().catch(() => null);

  if (!res.ok) {
    // EL ERROR SE DICE EN IDIOMA DE PERSONA (PANEL-LINEA-T1, pedido de Josse).
    //
    // Josse vio en pantalla "Error 429" y nos lo trajo: a el no le dice nada.
    // Fui a buscarlo a la vista donde lo vio y NO ESTABA -- esa pantalla ya
    // pinta texto humano. Nace aqui, en el fallback de cuando la API no manda
    // mensaje, y por eso podia salir en cualquiera de las 49 pantallas.
    //
    // La API sigue mandando: cuando trae un mensaje propio se respeta, porque
    // sabe mas del caso que nosotros. El codigo crudo deja de ser el ultimo
    // recurso, y de todos modos viaja entero en `code` y en `details` para
    // quien este diagnosticando.
    const msg = data?.mensaje || data?.message || data?.detail || enCastellano(res.status);
    return fail(res.status, msg, data);
  }

  return ok(data);
}

/**
 * Un codigo HTTP dicho como se lo diria a alguien que no programa.
 *
 * Solo los que de verdad pueden llegarle a un usuario. Un codigo que no este
 * aqui cae en la frase general, que tampoco nombra el numero: si un dia
 * aparece un 418, el dueno lee algo que entiende y el numero sigue en `code`.
 */
function enCastellano(status) {
  switch (status) {
    case 400: return 'Faltan datos o alguno no tiene el formato que la API espera.';
    case 403: return 'Tu usuario no tiene permiso para esto.';
    case 404: return 'Eso no existe, o ya no esta donde se buscaba.';
    case 408: return 'La API tardo demasiado en responder.';
    case 409: return 'Alguien mas lo cambio mientras trabajabas. Volve a abrirlo antes de guardar.';
    case 413: return 'Lo que se envio es demasiado grande.';
    case 422: return 'Los datos llegaron completos pero la API no los acepta asi.';
    case 429: return 'Se hicieron demasiadas consultas seguidas. Espera un momento y volve a intentar.';
    case 500:
    case 502:
    case 503:
    case 504: return 'La API tuvo un problema de su lado. No es algo que hayas hecho mal.';
    default: return 'No pudimos completar la operacion.';
  }
}

function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * SUBIR UN ARCHIVO (multipart). Va aparte de request() a proposito: ahi el cuerpo SIEMPRE
 * se serializa a JSON y el Content-Type se escribe a mano. En multipart el navegador tiene
 * que poner el boundary el mismo, asi que escribir el Content-Type ROMPE la subida.
 * Lo demas es igual: el Authorization y el error dicho en idioma de persona.
 */
async function subirArchivo(path, formData) {
  const url = `${AppConfig.apiBase}${path}`;
  const headers = {};
  if (AppConfig.token) headers['Authorization'] = `Bearer ${AppConfig.token}`;

  let res;
  try {
    res = await fetch(url, { method: 'POST', headers, body: formData });
  } catch (err) {
    return fail('NETWORK', err?.message || 'Error de red');
  }

  const data = await res.json().catch(() => null);
  if (!res.ok) {
    // El API explica por que no acepta la imagen (peso, formato); ese texto manda.
    const msg = data?.error || data?.mensaje || data?.message || enCastellano(res.status);
    return fail(res.status, msg, data);
  }
  return ok(data);
}

/**
 * LO YA PEDIDO NO SE VUELVE A PEDIR (PANEL-CARGA-EFICIENCIA-T1, Ares #26782).
 *
 * Josse sentia que el panel se pegaba al volver al Dashboard. Medido el 14-oct (dueno, 5 idas y vueltas al
 * Inventario): cada vuelta pedia otra vez las mismas 19 consultas, y una salia DOS veces a la vez (las misiones
 * de la tabla NXS: la tarjeta y el widget piden lo mismo).
 *
 *   1. Un GET igual que ya va en camino no sale otra vez: el segundo espera la misma respuesta. Vale para todo el
 *      panel; quien manda signal (timeout propio) va aparte.
 *   2. Las rutas que alguien registra con reusarRutas() se reusan un rato corto (el Dashboard: 30 s). Pasado ese
 *      rato se piden de nuevo: lo reusado nunca queda viejo mas de lo que dice la regla.
 *   3. Cualquier POST/PUT/PATCH/DELETE borra lo reusado, antes y despues: despues de guardar se lee lo nuevo. Y un
 *      GET que salio ANTES de un cambio no se guarda (puede traer lo de antes).
 *   4. Solo se guarda lo que salio bien, y la clave lleva la sesion: otro usuario no hereda nada.
 */
const enVuelo = new Map();
const reusados = new Map();
let reglasDeReuso = [];
let cambios = 0;

function copia(res) {
  try { return structuredClone(res); } catch (e) { return JSON.parse(JSON.stringify(res)); }
}

function msDeReuso(path) {
  const regla = reglasDeReuso.find((r) => r.patron.test(path));
  return regla ? regla.ms : 0;
}

function pedirGet(path, options) {
  if (options && options.signal) return request('GET', path, null, 0, options);
  const clave = (AppConfig.token || '') + ' ' + path;
  const guardado = reusados.get(clave);
  if (guardado && guardado.hasta > Date.now()) return Promise.resolve(copia(guardado.res));
  if (guardado) reusados.delete(clave);
  const esperando = enVuelo.get(clave);
  if (esperando) {
    esperando.otros++;
    return esperando.pedido.then(copia);
  }
  const cambiosAlSalir = cambios;
  const entrada = { otros: 0, pedido: null };
  entrada.pedido = request('GET', path, null, 0, options).then((res) => {
    enVuelo.delete(clave);
    const ms = msDeReuso(path);
    if (res && res.ok && ms > 0 && cambios === cambiosAlSalir) reusados.set(clave, { hasta: Date.now() + ms, res: copia(res) });
    return res;
  }, (err) => {
    enVuelo.delete(clave);
    throw err;
  });
  enVuelo.set(clave, entrada);
  // Q #26828: si alguien mas espero esta misma respuesta, el primero TAMBIEN recibe copia; si no, el original (sin costo).
  return entrada.pedido.then((res) => (entrada.otros > 0 ? copia(res) : res));
}

function olvidarReusados() {
  cambios++;
  reusados.clear();
}

async function cambiar(method, path, body, options) {
  olvidarReusados();
  try {
    return await request(method, path, body, 0, options);
  } finally {
    olvidarReusados();
  }
}

/**
 * UNA ESCRITURA QUE NO PASA POR ApiBase TAMBIEN BORRA LO REUSADO (Q #26828). Los webhooks de n8n (reconteo, fondo,
 * memoria), las subidas multipart con cabeceras propias y AgentApiClient llaman fetch directo: sin esto, el Dashboard
 * podia mostrar hasta 30 s lo de antes de ese cambio. Un GET pasa derecho, sin borrar nada.
 */
export async function fetchQueCambia(url, opts) {
  const metodo = String((opts && opts.method) || 'GET').toUpperCase();
  if (metodo === 'GET' || metodo === 'HEAD') return fetch(url, opts);
  olvidarReusados();
  try {
    return await fetch(url, opts);
  } finally {
    olvidarReusados();
  }
}

/** [{ patron: RegExp sobre la ruta con su query, ms }]. Reemplaza las reglas anteriores. */
export function reusarRutas(reglas) {
  reglasDeReuso = (reglas || []).filter((r) => r && r.patron instanceof RegExp && r.ms > 0);
}

export const ApiBase = {
  get: (path, options) => pedirGet(path, options),
  postArchivo: async (path, formData) => {
    olvidarReusados();
    try { return await subirArchivo(path, formData); } finally { olvidarReusados(); }
  },
  post: (path, body, options) => cambiar('POST', path, body, options),
  put: (path, body, options) => cambiar('PUT', path, body, options),
  patch: (path, body, options) => cambiar('PATCH', path, body, options),
  del: (path, body, options) => cambiar('DELETE', path, body, options),
  fetchQueCambia,
  olvidarReusados
};
